'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, RefreshCw, Trophy, Skull, MousePointer, User, Users } from 'lucide-react';
import { VEGAS_STRATS, TEMPEST_CHALLENGES, LAN_CHALLENGES } from '@/lib/constants';

type Challenge = {
  id: number;
  title: string;
  description: string;
  difficulty: string;
};

const GAMES = [
  { id: 'vegas', label: 'R6 Vegas', icon: Target, data: VEGAS_STRATS, color: 'text-red-500', bg: 'bg-red-500' },
  { id: 'tempest', label: 'Tempest Rising', icon: Trophy, data: TEMPEST_CHALLENGES, color: 'text-emerald-500', bg: 'bg-emerald-500' },
  { id: 'lan', label: 'LAN Fun', icon: MousePointer, data: LAN_CHALLENGES, color: 'text-blue-500', bg: 'bg-blue-500' },
];

export default function GameRoulette() {
  const [activeGame, setActiveGame] = useState('vegas');
  const [currentStrat, setCurrentStrat] = useState<Challenge | null>(null);
  const [targetPlayer, setTargetPlayer] = useState<string | null>(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [players, setPlayers] = useState<string[]>([]);

  // Lade Spieler beim Start (und bei jedem Spin neu, falls sich was geändert hat)
  const fetchPlayers = async () => {
    try {
      const res = await fetch('/api/users');
      const data = await res.json();
      setPlayers(data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchPlayers(); }, []);

  const currentGameConfig = GAMES.find(g => g.id === activeGame) || GAMES[0];

  const spin = async () => {
    setIsSpinning(true);
    setCurrentStrat(null);
    setTargetPlayer(null);
    
    // Update player list just in case
    await fetchPlayers();

    setTimeout(() => {
      const pool = currentGameConfig.data;
      const randomStrat = pool[Math.floor(Math.random() * pool.length)];
      
      // Select Victim
      let victim = "ALLE SPIELER";
      if (players.length > 0) {
        // 80% Chance auf Einzelspieler, 20% Chance auf ALLE (für die Spannung)
        if (Math.random() > 0.2) {
           victim = players[Math.floor(Math.random() * players.length)];
        }
      }

      setTargetPlayer(victim);
      setCurrentStrat(randomStrat);
      setIsSpinning(false);
    }, 800);
  };

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 shadow-2xl h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <currentGameConfig.icon className={`${currentGameConfig.color} w-8 h-8`} />
          <h2 className="text-2xl font-bold text-white uppercase tracking-wider">Extras Roulette</h2>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-slate-950 p-1 rounded-lg">
        {GAMES.map((game) => (
          <button
            key={game.id}
            onClick={() => { setActiveGame(game.id); setCurrentStrat(null); setTargetPlayer(null); }}
            className={`flex-1 py-2 text-sm font-bold rounded transition-all ${activeGame === game.id ? 'bg-slate-800 text-white shadow' : 'text-slate-500 hover:text-slate-300'}`}
          >
            {game.label}
          </button>
        ))}
      </div>

      {/* Display Area */}
      <div className="flex-1 min-h-[220px] flex flex-col items-center justify-center border-2 border-dashed border-slate-700 rounded-lg p-8 bg-slate-950/50 mb-6 relative overflow-hidden">
        <AnimatePresence mode="wait">
          {!currentStrat && !isSpinning && (
            <motion.p 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="text-slate-400 text-center italic"
            >
              Drücke den Button, um das Schicksal herauszufordern!
            </motion.p>
          )}

          {isSpinning && (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 0.5, ease: "linear" }}
            >
              <RefreshCw className={`w-12 h-12 ${currentGameConfig.color}`} />
            </motion.div>
          )}

          {currentStrat && !isSpinning && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center z-10 w-full"
            >
              {/* Target Player Badge */}
              <div className="flex justify-center mb-4">
                 <div className={`flex items-center gap-2 px-4 py-1 rounded-full border ${targetPlayer === 'ALLE SPIELER' ? 'bg-blue-500/20 border-blue-500 text-blue-400' : 'bg-red-500/20 border-red-500 text-red-400'} font-black text-sm uppercase tracking-widest shadow-lg`}>
                    {targetPlayer === 'ALLE SPIELER' ? <Users className="w-4 h-4" /> : <User className="w-4 h-4" />}
                    {targetPlayer}
                 </div>
              </div>

              <div className={`inline-block px-3 py-1 rounded-full ${currentGameConfig.bg}/10 ${currentGameConfig.color} text-xs font-bold mb-3 border ${currentGameConfig.bg}/20`}>
                {currentStrat.difficulty.toUpperCase()}
              </div>
              <h3 className="text-3xl font-black text-white mb-3">{currentStrat.title}</h3>
              <p className="text-slate-300 leading-relaxed max-w-lg mx-auto">{currentStrat.description}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <button
        onClick={spin}
        disabled={isSpinning}
        className="w-full bg-green-600 hover:bg-green-500 disabled:bg-slate-700 text-white font-bold py-4 rounded-lg transition-all transform active:scale-95 flex items-center justify-center gap-2 shadow-lg"
      >
        <RefreshCw className={`w-5 h-5 ${isSpinning ? 'animate-spin' : ''}`} />
        NEUE CHALLENGE
      </button>
    </div>
  );
}
